// apps/fe/src/modules/catalog/hooks/use-base-filter.ts

'use client';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { EventBus } from '@/shared/utils';
import { CatalogEvents } from '../events';
import { ICourseData } from '../types/course-data';
import { CourseDataFilters } from '../types/filtered-data';
import { Instrument } from '../types/instrument';
import { toggleFilterValue, updateInstrumentsFilter } from '../utils/filter-utils';
import { CourseType } from '../types/course';
import { useAuthStore, useAuthenticatedFetch } from '@/auth';
import { createAuthenticatedCatalogServices } from '../services/api-services-authenticated';
import { Either, left, right } from '@shared-commons/utils';
import { filterResponseCache, pendingRequests, invalidateCaches } from '../utils/cache-utils';

/**
 * Base hook for filtering functionality in catalog screens.
 * Provides common filtering logic that can be reused by specialized hooks.
 */
export function useBaseFilter({
  eventBus,
  filters,
  initialItems = [],
  fetchDataFn,
  eventKeys,
  additionalParams = {},
  useAuthentication = false,
  isEnrichingData = false,
  onFilterChange,
  initialActiveFilters,
}: {
  eventBus: EventBus<CatalogEvents>;
  filters: any;
  initialItems?: ICourseData[];
  fetchDataFn: any;
  eventKeys: any;
  additionalParams?: Record<string, any>;
  useAuthentication?: boolean;
  isEnrichingData?: boolean;
  onFilterChange?: (filters: CourseDataFilters) => void;
  initialActiveFilters?: CourseDataFilters | null;
}) {
  const { user } = useAuthStore();
  const { fetchWithAuth } = useAuthenticatedFetch();
  const [filteredItems, setFilteredItems] = useState<ICourseData[]>(initialItems);
  const [loadingCatalogData, setLoadingCatalogData] = useState(false);

  // Initialize with preserved filters if available
  const [activeFilters, setActiveFilters] = useState<CourseDataFilters>(
    initialActiveFilters || {
      instruments: [],
      skillLevels: [],
      genres: [],
      topics: [],
      types: [],
    },
  );

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 25;

  // Memoize parent instruments to avoid recalculation
  const parentInstruments = useMemo(
    () => filters?.instruments?.filter((inst: Instrument) => !inst.parentInstrument) || [],
    [filters?.instruments],
  );

  // References for tracking component state
  const initialFetchDoneRef = useRef(false);
  const currentRequestIdRef = useRef<string>('');
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadMoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create authenticated services with token refresh
  const authServices = useMemo(
    () => createAuthenticatedCatalogServices(fetchWithAuth, eventBus),
    [fetchWithAuth, eventBus],
  );

  // Memoize additionalParams to avoid different references
  // Use JSON.stringify for stable reference equality
  const stableAdditionalParams = useMemo(
    () => additionalParams,
    [JSON.stringify(additionalParams)],
  );

  // Sync filter changes to parent component if callback is provided
  const handleSetActiveFilters = useCallback(
    (newFilters: CourseDataFilters | ((prev: CourseDataFilters) => CourseDataFilters)) => {
      setActiveFilters(newFilters);
      if (onFilterChange) {
        // If newFilters is a function, we need to apply it to get the actual value
        onFilterChange(typeof newFilters === 'function' ? newFilters(activeFilters) : newFilters);
      }
    },
    [onFilterChange, activeFilters],
  );

  // Common filter handler
  const handleFilter = useCallback(
    ({ filters: incomingFilters }: { filters: Partial<CourseDataFilters> }) => {
      handleSetActiveFilters((prev) => ({ ...prev, ...incomingFilters }));
      setPage(1);
      setFilteredItems([]);
      setHasMore(true);
    },
    [handleSetActiveFilters],
  );

  // Common loader function with debounce
  const loadMore = useCallback(() => {
    if (!loadingCatalogData && hasMore) {
      // Prevent multiple rapid loadMore calls
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }

      loadMoreTimeoutRef.current = setTimeout(() => {
        setPage((prev) => prev + 1);
        loadMoreTimeoutRef.current = null;
      }, 150);
    }
  }, [loadingCatalogData, hasMore]);

  // Common intersection observer for infinite scroll
  const observer = useRef<IntersectionObserver | null>(null);
  const lastCourseElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loadingCatalogData) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMore();
          }
        },
        { rootMargin: '300px' },
      ); // Preload earlier for smoother experience

      if (node) observer.current.observe(node);
    },
    [loadingCatalogData, hasMore, loadMore],
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(activeFilters).some((val) =>
      Array.isArray(val) ? val.length > 0 : !!val,
    );
  }, [activeFilters]);

  // Create a cache key for this specific filter request
  const createCacheKey = useCallback(
    (page: number, filters: CourseDataFilters) => {
      return JSON.stringify({
        useAuthentication,
        userEmail: user?.email,
        page,
        filters,
        additionalParams: stableAdditionalParams,
        fetchFnName: fetchDataFn.name,
      });
    },
    [user?.email, useAuthentication, stableAdditionalParams, fetchDataFn.name],
  );

  // Data fetching effect
  useEffect(() => {
    // Skip if no fetch function
    if (!fetchDataFn) {
      return;
    }

    // SEARCH SPECIFIC: Always allow initial search, even without filters
    const isSearchRequest = eventKeys.error === 'catalog/search/error';
    const hasSearchQuery =
      stableAdditionalParams && 'query' in stableAdditionalParams && stableAdditionalParams.query;

    // Skip condition for non-search requests only
    if (!isSearchRequest && useAuthentication && !hasActiveFilters && page === 1) {
      console.log('Skipping filter fetch - no active filters for authenticated user');
      return;
    }

    // Skip fetching if enriching data is in progress
    if (isEnrichingData) {
      console.log('Skipping filter fetch while enriching data');
      return;
    }

    // Skip if on first page with no active filters (after initial fetch is done) - but NOT for search
    if (!isSearchRequest && page === 1 && !hasActiveFilters && initialFetchDoneRef.current) {
      return;
    }

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Generate a unique request ID to prevent concurrent overlapping requests
    const requestId = Date.now().toString();
    currentRequestIdRef.current = requestId;

    // Create cache key
    const cacheKey = createCacheKey(page, activeFilters);

    // Check cache for initial page load
    if (page === 1 && filterResponseCache.has(cacheKey)) {
      const cachedData = filterResponseCache.get(cacheKey);
      setFilteredItems(cachedData);
      setLoadingCatalogData(false);
      initialFetchDoneRef.current = true;
      return;
    }

    setLoadingCatalogData(true);

    const fetchData = async () => {
      try {
        // If another request has started or component unmounted, abort this one
        if (currentRequestIdRef.current !== requestId || !isMountedRef.current) {
          console.log('Aborting outdated request', requestId);
          return;
        }

        // Initialize res with a default value
        let res: Either<Error, any> = left(new Error('Data fetch not attempted'));

        // Use authenticated service if requested and user is logged in
        if (useAuthentication && user?.email) {
          let result;
          const additionalParamsArray = Object.values(stableAdditionalParams);

          // Check error key value to determine which endpoint to use
          if (eventKeys.error === 'catalog/filter/error') {
            console.log('Using getUserFilteredData endpoint with authentication');
            result = await authServices.getUserFilteredData(user.email, activeFilters, {
              page,
              pageSize,
            });
          } else if (eventKeys.error === 'catalog/search/error') {
            console.log('Using getUserSearchFilteredData endpoint with authentication');
            const query = additionalParamsArray[0];
            result = await authServices.getUserSearchFilteredData(
              user.email,
              activeFilters,
              { page, pageSize },
              query,
            );
          } else if (eventKeys.error === 'catalog/category/error') {
            console.log('Using getUserCategoryFilteredData endpoint with authentication');
            const categoryName = additionalParamsArray[0];
            result = await authServices.getUserCategoryFilteredData(
              user.email,
              activeFilters,
              { page, pageSize },
              categoryName,
            );
          } else {
            // Default to regular fetch when no matching authenticated service
            console.log(
              'Falling back to non-authenticated fetch even though useAuthentication=true',
            );
            res = await fetchDataFn(
              activeFilters,
              { page, pageSize },
              ...Object.values(stableAdditionalParams),
            )();
          }

          if (result) {
            if (!result.success) {
              res = left(result.error || new Error('Error fetching data'));
            } else {
              res = right(result.data);
            }
          }
        } else {
          // Use regular non-authenticated fetch
          console.log('Using non-authenticated fetch endpoint');
          res = await fetchDataFn(
            activeFilters,
            { page, pageSize },
            ...Object.values(stableAdditionalParams),
          )();
        }

        // Check if still the most recent request and component mounted
        if (currentRequestIdRef.current !== requestId || !isMountedRef.current) return;

        // Process response and update state
        if (res._tag === 'Left') {
          if (page === 1) setFilteredItems([]);
          eventBus.emit(eventKeys.error, res.value);
          setHasMore(false);
        } else {
          // Extract data based on response structure
          let newData = [];

          // Case 1: res.value is the array of items directly
          if (Array.isArray(res.value)) {
            newData = res.value;
          }
          // Case 2: res.value.data is the array of items
          else if (res.value && 'data' in res.value && Array.isArray(res.value.data)) {
            newData = res.value.data;
          }
          // Case 3: res.value.data.data is the array of items (nested structure from API)
          else if (
            res.value &&
            'data' in res.value &&
            typeof res.value.data === 'object' &&
            res.value.data !== null &&
            'data' in res.value.data &&
            Array.isArray(res.value.data.data)
          ) {
            newData = res.value.data.data;
          }

          // Cache first page results
          if (page === 1) {
            filterResponseCache.set(cacheKey, newData);
          }

          setFilteredItems((prev) => {
            if (page === 1) {
              return newData;
            } else {
              // Prevent duplicates when adding more items (pagination)
              const prevIds = new Set(prev.map((item: ICourseData) => item.documentId));
              const uniqueNewData = newData.filter(
                (item: ICourseData) => !prevIds.has(item.documentId),
              );

              if (uniqueNewData.length === 0) {
                return prev;
              }

              return [...prev, ...uniqueNewData];
            }
          });

          eventBus.emit(eventKeys.success, undefined);

          // Determine if there's more data to load based on number of items received
          const dataLength = newData.length;
          setHasMore(!(dataLength <= pageSize / 3));
        }

        initialFetchDoneRef.current = true;
      } catch (error) {
        // Only update state if this is still the current request and component mounted
        if (currentRequestIdRef.current === requestId && isMountedRef.current) {
          if (page === 1) setFilteredItems([]);
          const err = error instanceof Error ? error : new Error(String(error));
          eventBus.emit(eventKeys.error, err);
          setHasMore(false);
        }
      } finally {
        // Only update loading state if this is still the current request and component mounted
        if (currentRequestIdRef.current === requestId && isMountedRef.current) {
          setLoadingCatalogData(false);
        }
      }
    };

    fetchData();

    return () => {
      // Cleanup function
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    // Essential dependencies only
    activeFilters,
    page,
    fetchDataFn,
    useAuthentication,
    user?.email,
    JSON.stringify(stableAdditionalParams),
    isEnrichingData,
    eventBus,
    eventKeys,
    hasActiveFilters,
    authServices,
    pageSize,
    createCacheKey,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  // Common filter change handlers - all memoized with useCallback
  const instrumentsFilterOnChange = useCallback(
    (checked: boolean, instrument: Instrument) => {
      const newInstruments = updateInstrumentsFilter(
        checked,
        instrument,
        activeFilters.instruments,
        filters.instruments,
      );
      handleFilter({ filters: { instruments: newInstruments } });
    },
    [activeFilters.instruments, filters?.instruments, handleFilter],
  );

  const skillLevelFilterOnChange = useCallback(
    (checked: boolean, skillLevel: string) => {
      const newSkillLevels = toggleFilterValue(activeFilters.skillLevels, skillLevel, checked);
      handleFilter({ filters: { skillLevels: newSkillLevels } });
    },
    [activeFilters.skillLevels, handleFilter],
  );

  const musicalGenreFilterOnChange = useCallback(
    (checked: boolean, genre: string) => {
      const newGenres = toggleFilterValue(activeFilters.genres, genre, checked);
      handleFilter({ filters: { genres: newGenres } });
    },
    [activeFilters.genres, handleFilter],
  );

  const topicFilterOnChange = useCallback(
    (checked: boolean, topic: string) => {
      const newTopics = toggleFilterValue(activeFilters.topics, topic, checked);
      handleFilter({ filters: { topics: newTopics } });
    },
    [activeFilters.topics, handleFilter],
  );

  const courseTypeFilterOnChange = useCallback(
    (checked: boolean, courseType: CourseType) => {
      const newTypes = toggleFilterValue(activeFilters.types, courseType, checked) as CourseType[];
      handleFilter({ filters: { types: newTypes } });
    },
    [activeFilters.types, handleFilter],
  );

  // Add these methods to make the hook API consistent with other filtering hooks
  const handleSkillLevelsChange = useCallback(
    (selectedLevels: string[] | null) => {
      handleFilter({ filters: { skillLevels: selectedLevels || [] } });
    },
    [handleFilter],
  );

  const handleGenresChange = useCallback(
    (genre: string, checked: boolean) => {
      const newGenres = toggleFilterValue(activeFilters.genres, genre, checked);
      handleFilter({ filters: { genres: newGenres } });
    },
    [activeFilters.genres, handleFilter],
  );

  const handleTopicsChange = useCallback(
    (topic: string, checked: boolean) => {
      const newTopics = toggleFilterValue(activeFilters.topics, topic, checked);
      handleFilter({ filters: { topics: newTopics } });
    },
    [activeFilters.topics, handleFilter],
  );

  const handleTypeChange = useCallback(
    (type: string, checked: boolean) => {
      const newTypes = toggleFilterValue(activeFilters.types, type, checked) as CourseType[];
      handleFilter({ filters: { types: newTypes } });
    },
    [activeFilters.types, handleFilter],
  );

  const onResetSidebarFilters = useCallback(() => {
    handleFilter({
      filters: {
        skillLevels: [],
        genres: [],
        topics: [],
        types: [],
      },
    });
  }, [handleFilter]);

  const isAnyFilterActive = useMemo(() => hasActiveFilters, [hasActiveFilters]);

  return {
    filteredItems,
    loadingCatalogData,
    activeFilters,
    hasMore,
    page,
    setPage,
    handleFilter,
    instrumentsFilterOnChange,
    skillLevelFilterOnChange,
    musicalGenreFilterOnChange,
    topicFilterOnChange,
    courseTypeFilterOnChange,
    lastCourseElementRef,
    loadMore,
    parentInstruments,
    handleSkillLevelsChange,
    handleGenresChange,
    handleTopicsChange,
    handleTypeChange,
    onResetSidebarFilters,
    isAnyFilterActive,
  };
}
